"use client";

import { ArrowUpRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 3,
  orderby: "date",
  order: "desc"
};
const wp_query = new WP_Query(wp_query_params);
const fallbackImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/pet_weight_tracker.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/pet_weight_tracker.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/pet_weight_tracker.png-wvc-sizes"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/dog_walking_log.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/dog_walking_log.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/dog_walking_log.png-wvc-sizes"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/cat_playtime_tracker.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/cat_playtime_tracker.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704107/img/cat_playtime_tracker.png-wvc-sizes"
}];
const fallbackOrigins = ["ESSENTIAL · TRACKERS", "ACTIVITY · LOGS", "WELLNESS · TOOLS"];
const fallbackNotes = ["Monitor your pet's progress with ease", "Keep track of daily exercise and movement", "Engage your cat with structured play sessions"];
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] rounded-lg bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground">
        Failed to load resources.{" "}
        <Button variant="link" onClick={refetch} className="px-1 text-foreground underline">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-muted-foreground italic font-default">
        No digital resources available at the moment.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const fallback = fallbackImages[i % fallbackImages.length];
      const origin = product.categories?.[0]?.name?.toUpperCase() || fallbackOrigins[i % fallbackOrigins.length];
      const note = fallbackNotes[i % fallbackNotes.length];
      const imageSrc = product.featured_image_url || fallback.src;
      return <article data-index={i} className="group relative flex flex-col rounded-lg border border-border bg-card transition-colors duration-300 hover:border-[var(--chart-1)]">
            <Link to={product.link || "#"} className="block">
              <div className="relative aspect-square overflow-hidden rounded-t-lg bg-[var(--muted)]">
                <img src={imageSrc} {...product.featured_image_url ? {} : {
              "data-wvc-srcset": fallback.srcset,
              "data-wvc-sizes": fallback.sizes
            }} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[400ms] group-hover:opacity-0" />
                <img src={imageSrc} {...product.featured_image_url ? {} : {
              "data-wvc-srcset": fallback.srcset,
              "data-wvc-sizes": fallback.sizes
            }} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-opacity duration-[400ms] group-hover:opacity-100" />
              </div>
            </Link>

            <div className="flex flex-1 flex-col p-6">
              <span className="inline-flex w-fit items-center rounded-md bg-[var(--accent)] px-3 py-1 font-mono text-[0.65rem] tracking-[0.15em] text-[var(--accent-foreground)]">
                {origin}
              </span>

              <Link to={product.link || "#"} className="mt-4 block">
                <h3 className="font-default text-2xl leading-tight text-foreground transition-colors group-hover:text-[var(--chart-1)]">
                  {product.title}
                </h3>
              </Link>

              <p className="mt-2 font-default text-sm italic text-[var(--muted-foreground)]">
                {note}
              </p>

              <div className="my-6 h-px w-full bg-border" />

              <div className="mt-auto flex items-center justify-between">
                <div className="font-mono text-lg tabular-nums text-foreground">
                  $
                  {(product.discounted_price ?? product.price ?? 0).toFixed(2)}
                </div>
                <div className="relative">
                  <AddToCartButton productId={product.id} variant="default" size="icon" text="" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg h-10 w-10 transition-transform duration-300 hover:rotate-90" />
                </div>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function EssentialDigitalTools() {
  return <section data-section-id="56" id="essential-digital-tools" className="bg-background py-32">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--muted-foreground)]">
              WELLNESS · RESOURCES
            </div>
            <h2 className="mt-4 font-default text-4xl leading-[1.05] text-foreground sm:text-5xl lg:text-[3.5rem]">
              Essential Digital Tools
            </h2>
          </div>

          <div className="flex items-center gap-6">
            <Link to="/ShopPage" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-foreground transition-colors hover:text-[var(--chart-1)]">
              VIEW ALL TOOLS
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>

            <div className="hidden items-center gap-2 md:flex">
              <span className="h-2 w-2 rounded-lg bg-foreground" />
              <span className="h-2 w-2 rounded-lg bg-border" />
              <span className="h-2 w-2 rounded-lg bg-border" />
            </div>
          </div>
        </div>

        <div className="mt-16">
          <EcommerceProductsProvider wp_query={{
          per_page: 3,
          orderby: "date",
          order: "desc"
        }}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>;
}